import {
  Bank,
  BankAccountType,
  BonusAcceptanceHandling,
  BranchPasswordPolicySettings,
  Country,
  Language,
  PersonGender,
  PixKeyType,
  RequiredFieldValidation,
  RequiredUserField,
  SecurityQuestion,
  TimeZone,
} from '@arland-bmnext/api-data'
import { Translate } from 'next-translate'
import { ReactNode } from 'react'
import { FormFieldConfig, FormFieldType, parseNumberConverter } from '../components/forms/FormBuilder'
import { EnumTranslator } from '../util/enum-translator'
import { convertToSelectOptions } from '../util/select-option'
import {
  BirthdayValidator,
  BooleanValidator,
  CpfValidator,
  EmailPattern,
  NumberValidator,
  PasswordValidator,
  PhoneNumberValidator,
} from '../util/validators'
import { RequiredUserFields } from './api-client/account'

export const buildFormFieldFromRequiredField = (
  t: Translate,
  isRegistrationOrInquiry: boolean,
  requiredField: RequiredUserField,
  additional?: {
    countries?: Country[]
    nodeCountries?: Country[]
    securityQuestions?: SecurityQuestion[]
    languages?: Language[]
    timeZones?: TimeZone[]
    passwordPolicy?: BranchPasswordPolicySettings
    pixKeyType?: PixKeyType
    banks?: Bank[]
    autoFillAddressFromZipCode?: boolean
  },
): FormFieldConfig => {
  let formField: FormFieldConfig = {} as any

  // const emptySelectOption: SelectOption = {
  //   name: t('account:SelectPlaceholder'),
  //   value: "",
  // }

  switch (requiredField.objectProperty) {
    case RequiredUserFields.LOGONNAME:
      formField.name = t('account:Account.username')
      formField.property = 'logonName'
      formField.autocomplete = 'username'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.PASSWORD:
      formField.name = t('account:Account.password')
      formField.property = 'password'
      formField.formFieldType = FormFieldType.Input
      formField.autocomplete = 'new-password'
      formField.type = 'password'
      break
    case RequiredUserFields.EMAIL:
      formField.name = t('account:Account.email')
      formField.property = 'emailAddresse'
      formField.formFieldType = FormFieldType.Input
      formField.type = 'email'
      formField.inputType = 'email'
      break
    case RequiredUserFields.INITIAL:
      formField.name = t('account:Account.initial')
      formField.property = 'initial'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.LASTNAME:
      formField.name = t('account:Account.lastName')
      formField.property = 'lastName'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.FIRSTNAME:
      formField.name = t('account:Account.firstName')
      formField.property = 'firstName'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.NATIONALITY:
      formField.name = t('account:Account.nationality')
      formField.property = 'nationalityCountryId'
      formField.formFieldType = FormFieldType.Select
      formField.selectOptions = convertToSelectOptions(additional?.countries)
      formField.valueConverter = parseNumberConverter
      break
    case RequiredUserFields.PRIMARYADDRESS:
      formField.name = t('account:Account.streetAddress')
      formField.property = 'streetAddress'
      formField.formFieldType = additional?.autoFillAddressFromZipCode
        ? FormFieldType.StreetAddress
        : FormFieldType.Input
      formField.readonly = additional?.autoFillAddressFromZipCode
      break
    case RequiredUserFields.PRIMARYLOCALITY:
      formField.name = t('account:Account.locality')
      formField.property = 'locality'
      formField.formFieldType = FormFieldType.Input
      formField.readonly = additional?.autoFillAddressFromZipCode
      break
    case RequiredUserFields.PRIMARYZIPCODE:
      formField.name = t('account:Account.zipCode')
      formField.property = 'zipCode'
      formField.formFieldType = additional?.autoFillAddressFromZipCode ? FormFieldType.ZipCode : FormFieldType.Input
      break
    case RequiredUserFields.PRIMARYREGION:
      formField.name = t('account:Account.region')
      formField.property = 'region'
      formField.formFieldType = FormFieldType.Input
      formField.readonly = additional?.autoFillAddressFromZipCode
      break
    case RequiredUserFields.PRIMARYCITY:
      formField.name = t('account:Account.city')
      formField.property = 'city'
      formField.formFieldType = FormFieldType.Input
      formField.readonly = additional?.autoFillAddressFromZipCode
      break
    case RequiredUserFields.PRIMARYCOUNTRY:
      formField.name = t('account:Account.country')
      formField.property = 'countryId'
      formField.formFieldType = FormFieldType.Select
      formField.selectOptions = convertToSelectOptions(additional?.nodeCountries)
      formField.valueConverter = parseNumberConverter
      break
    case RequiredUserFields.GENDER:
      formField.name = t('account:Account.gender')
      formField.property = 'gender'
      formField.formFieldType = FormFieldType.Select
      formField.selectOptions = [
        { name: EnumTranslator(t, PersonGender, PersonGender.Male), value: PersonGender.Male },
        { name: EnumTranslator(t, PersonGender, PersonGender.Female), value: PersonGender.Female },
        { name: EnumTranslator(t, PersonGender, PersonGender.Other), value: PersonGender.Other },
      ]
      formField.valueConverter = parseNumberConverter
      break
    case RequiredUserFields.TITLE:
      formField.name = t('account:Account.title')
      formField.property = 'title'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.RECOVERYANSWER:
      formField.name = t('account:Account.passwordRecoveryAnswer')
      formField.property = 'passwordRecoveryAnswer'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.RECOVERYQUESTION:
      formField.name = t('account:Account.passwordRecoveryQuestion')
      formField.property = 'passwordRecoveryQuestionId'
      formField.formFieldType = FormFieldType.Select
      formField.selectOptions = convertToSelectOptions(additional?.securityQuestions)
      formField.valueConverter = parseNumberConverter
      break
    case RequiredUserFields.PHONENUMBER:
      formField.name = t('account:Account.phoneNumber')
      formField.property = 'phoneNumber'
      formField.formFieldType = FormFieldType.PhoneNumber
      formField.type = 'tel'
      formField.inputType = 'tel'
      formField.countries = additional?.nodeCountries
      break
    case RequiredUserFields.LANGUAGE:
      formField.name = t('account:Account.language')
      formField.property = 'languageId'
      formField.formFieldType = FormFieldType.Select
      formField.selectOptions = convertToSelectOptions(additional?.languages)
      formField.valueConverter = parseNumberConverter
      break
    case RequiredUserFields.TIMEZONE:
      formField.name = t('account:Account.timezone')
      formField.property = 'timeZoneId'
      formField.formFieldType = FormFieldType.Select
      formField.selectOptions = convertToSelectOptions(additional?.timeZones)
      formField.valueConverter = parseNumberConverter
      break
    case RequiredUserFields.BETPIN:
      formField.name = t('account:Account.betPin')
      formField.property = 'betPin'
      formField.formFieldType = FormFieldType.Input
      formField.inputType = 'numeric'
      break
    case RequiredUserFields.CARDNUMBER:
      formField.name = t('account:Account.cardNumber')
      formField.property = 'cardNumber'
      formField.formFieldType = FormFieldType.Input
      formField.inputType = 'numeric'
      break
    case RequiredUserFields.PHONEPIN:
      formField.name = t('account:Account.phonePin')
      formField.property = 'phonePin'
      formField.formFieldType = FormFieldType.Input
      formField.inputType = 'numeric'
      break
    case RequiredUserFields.RFIDNUMBER:
      formField.name = t('account:Account.rfidNumber')
      formField.property = 'rfidNumber'
      formField.formFieldType = FormFieldType.Input
      formField.inputType = 'numeric'
      break
    case RequiredUserFields.SECONDNAME:
      formField.name = t('account:Account.secondName')
      formField.property = 'secondName'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.BIRTHDAY:
      formField.name = t('account:Account.birthDay')
      formField.property = 'birthday'
      formField.formFieldType = FormFieldType.DateSelect
      formField.type = 'date'
      break
    case RequiredUserFields.BIRTHPLACE:
      formField.name = t('account:Account.birthPlace')
      formField.property = 'birthPlace'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.BIRTHNAME:
      formField.name = t('account:Account.birthName')
      formField.property = 'birthName'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.NATIONALIDENTITYID:
      formField.name = t('account:Account.nationalIdentityId')
      formField.property = 'nationalIdentityId'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.PIXKEY:
      formField.name = t('account:Account.pixKey')
      formField.valueConverter = {
        setValue(val, setValue) {
          const type = val['pixKeyType']

          switch (type) {
            case PixKeyType.Cpf:
              setValue('pixKey_cpf', val['pixKey'])
              break
            case PixKeyType.Email:
              setValue('pixKey_email', val['pixKey'])
              break
            case PixKeyType.Telefone:
              setValue('pixKey_phone', val['pixKey'])
              break
          }
        },
        fromForm(val, formData, key) {
          const type = formData['pixKeyType']

          switch (type) {
            case PixKeyType.Cpf:
              return formData['pixKey_cpf']
            case PixKeyType.Email:
              return formData['pixKey_email']
            case PixKeyType.Telefone:
              return formData['pixKey_phone']
          }
        },
      }
      break
    case RequiredUserFields.PIXKEYTYPE:
      formField.name = t('account:Account.pixKeyType')
      formField.property = 'pixKeyType'
      formField.formFieldType = FormFieldType.RadioGroupElement
      formField.selectOptions = [
        { name: t('account:PixKeyType.Cpf'), value: PixKeyType.Cpf },
        { name: t('account:PixKeyType.Email'), value: PixKeyType.Email },
        { name: t('account:PixKeyType.Phone'), value: PixKeyType.Telefone },
      ]
      break
    case RequiredUserFields.DIVIDER:
      formField.name = ''
      formField.property = ''
      formField.formFieldType = FormFieldType.Divider
      break
    case RequiredUserFields.BANK_ID:
      formField.name = t('account:Account.bankId')
      formField.property = 'bankId'
      formField.formFieldType = FormFieldType.SearchableSelect

      formField.selectOptions = convertToSelectOptions(additional.banks)
      formField.valueConverter = parseNumberConverter
      break
    case RequiredUserFields.BANK_CODE:
      formField.name = t('account:Account.bankCode')
      formField.property = 'bankCode'
      formField.formFieldType = FormFieldType.Input
      break
    case RequiredUserFields.BANK_ACCOUNT_TYPE:
      formField.name = t('account:Account.bankAccountType')
      formField.property = 'bankAccountType'
      formField.formFieldType = FormFieldType.Select
      formField.valueConverter = parseNumberConverter
      formField.selectOptions = [
        {
          name: EnumTranslator(t, BankAccountType, BankAccountType.RegularAccount),
          value: BankAccountType.RegularAccount,
        },
        {
          name: EnumTranslator(t, BankAccountType, BankAccountType.SavingAccount),
          value: BankAccountType.SavingAccount,
        },
        {
          name: EnumTranslator(t, BankAccountType, BankAccountType.PaymentAccount),
          value: BankAccountType.PaymentAccount,
        },
      ]
      break
    case RequiredUserFields.BANK_ACCOUNT_NUMBER:
      formField.name = t('account:Account.bankAccountNumber')
      formField.property = 'bankAccountNumber'
      /*formField.valueConverter = {
        setValue(val, setValue) {
          if (val) {
            setValue('bankAccountNumber', val.bankAccountNumber)
            setValue('accountSecurityCode', val.accountSecurityCode)
          }
        },
      }
      formField.formFieldType = FormFieldType.BankAccountNumber*/
      formField.formFieldType = FormFieldType.Input
      formField.type = 'numeric'
      break
  }

  formField.validators = {}

  if (
    (requiredField.validation & RequiredFieldValidation.NotEmpty) === RequiredFieldValidation.NotEmpty ||
    !requiredField.optional
  ) {
    formField.required = true
  }

  if ((requiredField.validation & RequiredFieldValidation.Regex) === RequiredFieldValidation.Regex) {
    formField.pattern = new RegExp(requiredField.regEx)
  }

  if ((requiredField.validation & RequiredFieldValidation.EmailAddress) === RequiredFieldValidation.EmailAddress) {
    formField.pattern = EmailPattern
    formField.customPatternErrorMessage = t('account:Account.emailPatternError')
  }

  if ((requiredField.validation & RequiredFieldValidation.Maximum) === RequiredFieldValidation.Maximum) {
    formField.maxLength = requiredField.maximum
  }

  if ((requiredField.validation & RequiredFieldValidation.Minimum) === RequiredFieldValidation.Minimum) {
    formField.minLength = requiredField.minimum
  }

  if (requiredField.objectProperty === RequiredUserFields.PASSWORD) {
    formField.validators = PasswordValidator(t, additional?.passwordPolicy)
  }

  if (requiredField.objectProperty === RequiredUserFields.PIXKEYTYPE && !requiredField.optional) {
    formField.minValue = 1
  }

  if (requiredField.objectProperty === RequiredUserFields.NATIONALIDENTITYID && requiredField.config === 'cpf') {
    formField.formFieldType = FormFieldType.CpfInput
    formField.minLength = undefined
    formField.maxLength = undefined
    formField.pattern = undefined
    formField.validators = CpfValidator(t)
  }

  if (requiredField.objectProperty === RequiredUserFields.BETPIN && additional?.passwordPolicy != null) {
    formField.minLength = additional?.passwordPolicy?.pinCodeLength
    formField.maxLength = additional?.passwordPolicy?.pinCodeLength
  }

  if (requiredField.objectProperty === RequiredUserFields.PHONENUMBER) {
    formField.minLength = undefined
    formField.maxLength = undefined
    formField.validators = PhoneNumberValidator(t, requiredField.minimum, requiredField.maximum)
  }

  if (requiredField.objectProperty === RequiredUserFields.BIRTHDAY) {
    formField.validators = BirthdayValidator(t)
  }

  if (requiredField.objectProperty === RequiredUserFields.PIXKEY && additional?.pixKeyType != null) {
    if (additional.pixKeyType === PixKeyType.Telefone) {
      formField.property = 'pixKey_phone'
      formField.formFieldType = FormFieldType.PhoneNumber
      formField.type = 'tel'
      formField.inputType = 'tel'
      formField.countries = additional?.nodeCountries
      formField.minLength = undefined
      formField.maxLength = undefined
      formField.pattern = undefined
      formField.validators = PhoneNumberValidator(t, requiredField.minimum, requiredField.maximum)
    } else if (additional.pixKeyType === PixKeyType.Email) {
      formField.property = 'pixKey_email'
      formField.formFieldType = FormFieldType.Input
      formField.type = 'email'
      formField.inputType = 'email'
      formField.pattern = EmailPattern
      formField.customPatternErrorMessage = t('account:Account.emailPatternError')
    } else if (additional.pixKeyType === PixKeyType.Cpf) {
      formField.property = 'pixKey_cpf'
      formField.formFieldType = FormFieldType.CpfInput
      formField.minLength = undefined
      formField.maxLength = undefined
      formField.pattern = undefined
      formField.disabled = true
      formField.validators = undefined
    }
  }

  if (requiredField.objectProperty === RequiredUserFields.CARDNUMBER) {
    formField.minLength = 12
    formField.maxLength = 12
  }

  if (!isRegistrationOrInquiry && !requiredField.isEditable) {
    formField.readonly = true
    formField.validators = null
  }

  if (requiredField.objectProperty === RequiredUserFields.BANK_ACCOUNT_NUMBER) {
    formField.validators = NumberValidator(t)
  }
  // if (requiredField.objectProperty === RequiredUserFields.PRIMARYADDRESS && isZipCodeAutoFillEnable) {
  //   formField.readonly = true
  //   formField.validators = null
  // }

  return formField
}

export const getDividerField = (): Partial<FormFieldConfig> => {
  return {
    name: '',
    property: '',
    formFieldType: FormFieldType.Divider,
  }
}

export const getTermsAndConditionsField = (t: Translate, helpTextDescription?: string | ReactNode): FormFieldConfig => {
  return {
    name: t('account:Account.termsAndConditions'),
    helpText: helpTextDescription
      ? {
          title: t('account:Account.termsAndConditions'),
          description: helpTextDescription,
        }
      : undefined,
    property: 'acceptTnc',
    formFieldType: FormFieldType.RadioGroupElement,
    selectOptions: [
      { name: t('common:Common.yes'), value: true },
      { name: t('common:Common.no'), value: false },
    ],
    showOptionalIfNotRequired: false,
    validators: BooleanValidator(true, t('account:Account.termsAndConditionsValidationMessage')),
  }
}

export const getSubcribeToNewsletterField = (
  t: Translate,
  helpTextDescription?: string | ReactNode,
): FormFieldConfig => {
  return {
    name: t('account:Account.sendNewsLetterEmail'),
    helpText: helpTextDescription
      ? {
          title: t('account:Account.sendNewsLetterEmail'),
          description: helpTextDescription,
        }
      : undefined,
    property: 'sendNewsLetterEmail',
    formFieldType: FormFieldType.RadioGroupElement,
    selectOptions: [
      { name: t('common:Common.yes'), value: true },
      { name: t('common:Common.no'), value: false },
    ],
  }
}

export const getFinancyMinistryText = (t: Translate, label?: string): FormFieldConfig => {
  return {
    name: label ? label : t('account:Account.acceptFinanceMinistry'),
    property: 'acceptDataSendToGov',
    formFieldType: FormFieldType.RadioGroupElement,
    selectOptions: [
      { name: t('common:Common.yes'), value: true },
      { name: t('common:Common.no'), value: false },
    ],
    showOptionalIfNotRequired: false,
    validators: BooleanValidator(true, t('account:Account.acceptFinanceMinistryValidationMessage')),
  }
}

export const getPromotionCodeField = (t: Translate) => {
  return {
    name: t('account:Account.promotionCode'),
    property: 'promotionCode',
    formFieldType: FormFieldType.Input,
  }
}

export const getBonusAcceptanceField = (t: Translate): FormFieldConfig => {
  return {
    name: t('account:BonusAcceptanceSelector.title'),
    helpText: {
      title: t('account:BonusAcceptanceSelector.title'),
      description: t('account:BonusAcceptanceSelector.description'),
    },
    property: 'bonusAcceptance',
    formFieldType: FormFieldType.RadioGroupElement,
    required: true,
    selectOptions: [
      { name: t('account:BonusAcceptanceHandling.AlwaysAccept'), value: BonusAcceptanceHandling.AlwaysAccept },
      { name: t('account:BonusAcceptanceHandling.AlwaysAsk'), value: BonusAcceptanceHandling.AlwaysAsk },
      { name: t('account:BonusAcceptanceHandling.NeverAccept'), value: BonusAcceptanceHandling.NeverAccept },
    ],
  }
}
